import { arrayMove } from '@dnd-kit/sortable'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Checkbox } from 'antd'
import { Flex, Popover, Typography } from 'antd'
import { setDynamicColumns, setVisualAppears } from 'app/tableSettingSlice'
import Utils from 'general/utils/Utils'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import useSearchDebounce from 'hooks/useSearchDebounce'
import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import CustomAntButton from '../Button/CustomAntButton'
import CustomAntInput from '../Forms/CustomAntInput/CustomAntInput'
import { IconAction } from '../GroupButton/styles'
import DnDColumnList from './components/DnDColumnList'
import { transformingElements } from './functions/helpers'

/**
 * @template T
 * @param {Object} props
 * @param {Array<T>} props.defaultColumns
 * @param {string} props.localStorageName
 * @returns {JSX.Element}
 */

function AppDnD({ defaultColumns, localStorageName }) {
  const { t } = useTranslation()
  const accountId = useAppSelector((s) => s.auth.currentAccount.accountId)
  const key = useMemo(
    () => `${accountId}/${localStorageName}`,
    [accountId, localStorageName],
  )
  const detectLastItemIsActionCol =
    _.last(defaultColumns)?.disabled ||
    _.last(defaultColumns)?.name.toLocaleLowerCase() ===
      t('Action').toLocaleLowerCase() ||
    _.last(defaultColumns)?.name === ''

  const dispatch = useAppDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const defaultElements = useMemo(
    () =>
      transformingElements(
        detectLastItemIsActionCol ? _.initial(defaultColumns) : defaultColumns,
      ),
    [defaultColumns, detectLastItemIsActionCol],
  )
  const { visualAppears } = useAppSelector((s) => s.table)

  const { debouncedSearch, search, setSearch } = useSearchDebounce('', 300)

  /** Get local storage if there is any, otherwise set it to transformed element */
  const [snapshot, setSnapshot] = useLocalStorage(key, defaultElements)

  const [checkEditDraft, setCheckEditDraft] = useState(true)

  /** Set the draft version of the previous snapshots - This will be used
   * to do the actual reordering of the columns.
   * If user cancel changes, we could revert back to the previous state
   */
  const [draftColumns, setDraftColumns] = useState([])
  const [trackingState, setTrackingState] = useState([]) // ONLY FOR DYNAMIC COLUMNS
  const [countValidate, setCountValidate] = useState(0)

  useEffect(() => {
    setTrackingState((prev) => (_.isEqual(prev, snapshot) ? prev : snapshot))

    if (checkEditDraft) {
      setDraftColumns((prev) => (_.isEqual(prev, snapshot) ? prev : snapshot))
    }
  }, [snapshot, checkEditDraft])

  /** @type {import('react-beautiful-dnd').OnDragEndResponder} */
  function onDragEnd(result) {
    const { destination, source } = result
    if (!destination) return false

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return false
    // if (destination.index === visualAppears.length - 1) return false
    const newOrder = arrayMove(visualAppears, source.index, destination.index)
    setDraftColumns(newOrder)
  }

  function handleSetDefault() {
    setDraftColumns(defaultElements)
    setSearch('')
  }

  function handleCancel() {
    setDraftColumns(snapshot)
  }

  function handleSaveColumnState() {
    setSnapshot(visualAppears)
    setTrackingState(visualAppears)
    setSearch('')
    setIsOpen(false)
  }

  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setCheckEditDraft(false)
    }
  }, [isOpen, setSearch])

  useEffect(() => {
    dispatch(
      setVisualAppears(
        _.map(draftColumns, (column) => {
          return {
            ...column,
            isAppear: _.includes(
              Utils.convertString(column.name),
              Utils.convertString(debouncedSearch),
            ),
          }
        }),
      ),
    )
  }, [debouncedSearch, dispatch, draftColumns])

  useEffect(() => {
    if (countValidate == 1) {
      dispatch(setDynamicColumns(trackingState))
      dispatch(setVisualAppears(trackingState))
    } else if (countValidate == 0) {
      setCountValidate(countValidate + 1)
    }
  }, [dispatch, trackingState])

  return (
    <Popover
      arrow={false}
      placement="bottomRight"
      trigger="click"
      getPopupContainer={(trigger) => trigger.parentNode}
      open={isOpen}
      onOpenChange={(state) => {
        setIsOpen(state)
        handleCancel()
      }}
      overlayInnerStyle={{
        padding: 0,
      }}
      title={
        <Flex justify={'space-between'} align="center" className="p-6">
          <Typography.Title level={4} className="">
            Thiết lập cột
          </Typography.Title>
          <CustomAntButton
            customStyle={{
              minWidth: 'fit-content',
            }}
            iconCn="fa-regular fa-times"
            antProps={{
              type: 'text',
              onClick: () => {
                setIsOpen(false)
                handleCancel()
              },
            }}
          />
        </Flex>
      }
      content={
        <section>
          <div className="mx-6 mb-6">
            <CustomAntInput
              type="search"
              borderRadius="sm"
              inputProps={{
                name: 'dragAndDropSearch',
                autoComplete: 'off',
                placeholder: 'Tìm kiếm cột',
                value: search,
                onChange: (e) => setSearch(e.target.value),
              }}
            />
          </div>
          <DragDropContext
            onDragEnd={onDragEnd}
            enableDefaultSensors
            dragHandleUsageInstructions="Drag to reorder"
          >
            <Droppable droppableId="droppable" direction="vertical">
              {(droppableProvided) => (
                <ul
                  {...droppableProvided.droppableProps}
                  className="list-group mx-6 max-h-350px overflow-auto"
                  ref={droppableProvided.innerRef}
                >
                  <li className="list-group-item p-3 position-sticky top-0 z-2">
                    <Checkbox
                      checked={_.every(visualAppears, (v) => !v.hidden)}
                      onChange={({ target }) => {
                        setDraftColumns(
                          _.map(visualAppears, (v) => {
                            return {
                              ...v,
                              hidden: !target.checked,
                            }
                          }),
                        )
                      }}
                    >
                      Tất cả
                    </Checkbox>
                  </li>
                  <DnDColumnList key={key} />
                  {droppableProvided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
          {/* set default button */}
          <Flex
            wrap="wrap"
            justify="space-between"
            align="center"
            className="gap-3 py-3 px-6"
          >
            <CustomAntButton
              text="Đặt về mặc định"
              variant="success"
              antProps={{
                type: 'primary',
                onClick: handleSetDefault,
              }}
            />
            <CustomAntButton
              text="Lưu lại"
              antProps={{
                type: 'primary',
                onClick: handleSaveColumnState,
              }}
            />
          </Flex>
        </section>
      }
    >
      <CustomAntButton
        iconCn="fa-regular fa-gear"
        title="Thiết lập cột hiển thị"
        antProps={{
          type: 'default',
          className: 'p-3',
          shape: 'round',
          onClick: () => setIsOpen((prev) => !prev),
        }}
        customStyle={{
          minWidth: 'fit-content',
        }}
      />
    </Popover>
  )
}

export default AppDnD
